import { createFileRoute, redirect } from '@tanstack/react-router';
import { Loader, Stack, Table } from '@mantine/core';
import { IconRuler2Off } from '@tabler/icons-react';
import { EmptyState } from '../../../components/EmptyState';
import { PageHeader } from '../../../components/PageHeader';
import { CalibrationRow } from '../../../features/silo-counts/components/CalibrationRow';
import { useSiloLocations } from '../../../features/silo-counts/api';

/** Silo calibration config (BUILD_SPEC.md §5.2, §5.6) — this goal's sole
 * ownership (plan.md QC fix #3). Method LINEAR (height + full volume) or
 * STRAPPING (level→volume table), plus a density source (product bulk density
 * or a per-silo override); calls `PUT /config/locations/:id/silo-calibration`.
 *
 * `beforeLoad` guard (Stuart ruling 2026-08-26: silos default OFF per tenant)
 * — same `context.auth`-based redirect as status.tsx; see that file's comment
 * for why `beforeLoad` (not a render-time redirect) was chosen here. */
export const Route = createFileRoute('/_authenticated/silo-counts/calibration')({
  beforeLoad: ({ context }) => {
    if (context.auth.activeTenant?.settings.silosEnabled !== true) {
      throw redirect({ to: '/silo-counts/counts' });
    }
  },
  component: CalibrationPage,
});

function CalibrationPage() {
  const locationsQuery = useSiloLocations();
  const locations = locationsQuery.data?.items ?? [];

  return (
    <Stack gap="lg">
      <PageHeader
        title="Silo calibration"
        description="Set up how each silo's level reading converts into an estimated tonnage."
      />
      {locationsQuery.isLoading ? (
        <Loader size="sm" />
      ) : locations.length === 0 ? (
        <EmptyState
          icon={IconRuler2Off}
          title="No SILO locations configured yet"
          hint="Ask an admin to create one in Settings first."
        />
      ) : (
        <Table.ScrollContainer minWidth={560}>
          <Table verticalSpacing="xs" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Code</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Calibration</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {locations.map((location) => (
                <CalibrationRow key={location.id} location={location} />
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}
